import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Mail, Loader2, KeyRound, CheckCircle2, ArrowRight, Globe } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function EmergentAdminGate() {
  const { lang, toggle } = useI18n();
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("email"); // email, otp, done
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [hint, setHint] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get("/eadmin/l1-status").then((r) => { if (r.data.emergent_admin_authenticated) setStep("done"); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (countdown <= 0) return;
    timerRef.current = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [countdown]);

  const sendOtp = async (resend = false) => {
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post(resend ? "/eadmin/resend-otp" : "/eadmin/request-otp", { email });
      setHint(data.email_hint || ""); setStep("otp"); setCountdown(300);
      toast.success("Verification code sent to your authorized administrator email.");
    } catch (e) { setErr(formatError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  const verify = async () => {
    setErr(""); setLoading(true);
    try {
      await api.post("/eadmin/verify-otp", { email, code });
      await refresh();
      setStep("done");
      toast.success("Administrator identity verified");
    } catch (e) { setErr(formatError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  const mm = String(Math.floor(countdown / 60)).padStart(2, "0");
  const ss = String(countdown % 60).padStart(2, "0");

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden lg:flex flex-col justify-between qatar-gradient p-12 text-white">
        <div>
          <div className="mb-8 flex items-center gap-3"><div className="grid h-12 w-12 place-items-center rounded-2xl bg-white/15 text-2xl font-bold font-head">Q</div><p className="text-xs uppercase tracking-widest text-white/70">ITOO TECHNOLOGIES W.L.L.</p></div>
          <h1 className="max-w-md text-4xl font-bold font-head leading-tight">Enterprise AI Governance Operating System</h1>
          <p className="mt-4 max-w-sm text-sm text-white/80">Layer 1 — Emergent Admin Gateway. Verify your authorized administrator identity by email to continue. Portal access requires a second application login.</p>
        </div>
        <p className="text-xs text-white/60">DEMO / DEVELOPMENT. Verification codes are generated and delivered server-side and are never displayed, returned by the API, or logged.</p>
      </div>

      <div className="flex items-center justify-center bg-slate-50 p-6">
        <div className="w-full max-w-sm">
          <div className="mb-6 flex justify-end"><button data-testid="gate-lang" onClick={toggle} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600"><Globe className="h-4 w-4" /> {lang === "en" ? "العربية" : "English"}</button></div>

          {step === "email" && (
            <div className="animate-fade-up">
              <div className="mb-2 flex items-center gap-2 text-[var(--qatar)]"><ShieldCheck className="h-6 w-6" /><h2 className="text-2xl font-bold font-head text-slate-900">Secure Admin Access</h2></div>
              <p className="mb-6 text-sm text-slate-500">Verify your authorized administrator identity to continue.</p>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Administrator Email</label>
              <input data-testid="gate-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter email"
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-[var(--qatar)] focus:ring-2 focus:ring-[var(--qatar-border)]" />
              {err && <p data-testid="gate-error" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{err}</p>}
              <button data-testid="gate-send-otp" onClick={() => sendOtp(false)} disabled={loading || !email} className="qatar-gradient mt-4 flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold text-white disabled:opacity-60">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4" /> Send OTP</>}
              </button>
            </div>
          )}

          {step === "otp" && (
            <div className="animate-fade-up">
              <div className="mb-2 flex items-center gap-2 text-[var(--qatar)]"><KeyRound className="h-6 w-6" /><h2 className="text-2xl font-bold font-head text-slate-900">Verify Admin Email</h2></div>
              <p className="mb-6 text-sm text-slate-500">A verification code has been sent to your authorized administrator email {hint && <span className="font-medium">({hint})</span>}.</p>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Verification Code</label>
              <input data-testid="gate-otp" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" maxLength={6} placeholder="______"
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-center text-lg font-mono tracking-[0.5em] outline-none focus:border-[var(--qatar)] focus:ring-2 focus:ring-[var(--qatar-border)]" />
              <p className="mt-2 text-xs text-slate-400">{countdown > 0 ? `Code expires in ${mm}:${ss}` : "Code expired — request a new one."}</p>
              {err && <p data-testid="gate-error" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{err}</p>}
              <button data-testid="gate-verify-otp" onClick={verify} disabled={loading || code.length !== 6} className="qatar-gradient mt-4 flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold text-white disabled:opacity-60">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify OTP"}
              </button>
              <button data-testid="gate-resend-otp" onClick={() => sendOtp(true)} disabled={loading || countdown > 240} className="mt-2 w-full rounded-xl border border-slate-200 py-2.5 text-sm font-medium text-slate-600 disabled:opacity-50">
                Resend OTP {countdown > 240 ? `(${countdown - 240}s)` : ""}
              </button>
            </div>
          )}

          {step === "done" && (
            <div className="animate-fade-up" data-testid="gate-done">
              <div className="mb-2 flex items-center gap-2 text-emerald-600"><CheckCircle2 className="h-7 w-7" /><h2 className="text-2xl font-bold font-head text-slate-900">Administrator identity verified</h2></div>
              <p className="mb-6 text-sm text-slate-500">Continue to the Enterprise AI Governance Operating System and sign in with your application credentials.</p>
              <button data-testid="gate-continue" onClick={() => nav("/application-login")} className="qatar-gradient flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold text-white">
                Continue <ArrowRight className="h-4 w-4 flip-x" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
